import type { ForecastInsights } from "../../hooks/useForecastInsights";
import { AlertTriangle, Lightbulb, Sparkles, TrendingUp } from "lucide-react";

type Props = {
  insights: ForecastInsights | null;
  isLoading: boolean;
};

const UUID_TOKEN_RE =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const OPAQUE_ID_RE = /(?<!\w)_[A-Za-z0-9_-]{12,}(?!\w)/g;

function stripInternalIdTokens(text: string): string {
  return text
    .replace(UUID_TOKEN_RE, "that category")
    .replace(OPAQUE_ID_RE, "that category")
    .replace(/\s+/g, " ")
    .replace(/(that category\s*){2,}/gi, "that category ")
    .replace(/\bcategory\s+that category\b/gi, "that category")
    .trim();
}

function formatInsightItem(item: unknown): string {
  if (typeof item === "string") {
    const trimmed = item.trim();
    if (!trimmed) return "No details";
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        return formatInsightItem(JSON.parse(trimmed));
      } catch {
        return stripInternalIdTokens(trimmed);
      }
    }
    return stripInternalIdTokens(trimmed);
  }

  if (!item || typeof item !== "object") {
    return "No details";
  }

  const record = item as Record<string, unknown>;
  const preferredKeys = [
    "description",
    "title",
    "idea",
    "hint",
    "message",
    "suggestion",
    "summary",
    "type",
  ];

  for (const key of preferredKeys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return stripInternalIdTokens(value);
    }
  }

  const parts = Object.entries(record)
    .filter(
      ([key, value]) =>
        typeof value === "string" &&
        value.trim() &&
        key !== "id" &&
        !key.toLowerCase().endsWith("id"),
    )
    .slice(0, 3)
    .map(([key, value]) => `${key}: ${String(value)}`);

  return parts.length > 0 ? stripInternalIdTokens(parts.join(" · ")) : "No details";
}

function formatExplanation(raw: unknown): string {
  if (typeof raw !== "string") return "No explanation returned.";
  const trimmed = raw.trim();
  if (!trimmed) return "No explanation returned.";
  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    try {
      return formatInsightItem(JSON.parse(trimmed));
    } catch {
      return stripInternalIdTokens(trimmed);
    }
  }
  return stripInternalIdTokens(trimmed);
}

export function SmartSuggestionsPanel({ insights, isLoading }: Props) {
  const growthHints = (insights?.growthHints || []).slice(0, 2);
  const anomalies = (insights?.anomalies || []).slice(0, 2);
  const scenarioIdeas = (insights?.scenarioIdeas || []).slice(0, 2);

  return (
    <section className="rounded-xl border border-slate-200 bg-linear-to-br from-white to-emerald-50/30 p-6">
      <div className="mb-6 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-violet-500" />
        <h3 className="text-lg font-semibold text-slate-900">
          Smart Suggestions
        </h3>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
          <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="rounded-xl border border-violet-200 bg-white/80 p-4">
            <p className="text-xs font-bold uppercase tracking-widest text-violet-600">
              AI-Assisted Insight
            </p>
            <p className="mt-2 text-sm italic text-slate-700">
              {formatExplanation(insights?.explanation)}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <div className="mb-2 flex items-center gap-2 text-blue-600">
                <TrendingUp className="h-3.5 w-3.5" />
                <h4 className="text-xs font-bold uppercase tracking-wide">
                  Growth hints
                </h4>
              </div>
              <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">
                {growthHints.map((item, idx) => (
                  <li key={`growth-${idx}`}>{formatInsightItem(item)}</li>
                ))}
              </ul>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" />
                <h4 className="text-xs font-bold uppercase tracking-wide">
                  Anomalies
                </h4>
              </div>
              <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">
                {anomalies.map((item, idx) => (
                  <li key={`anomaly-${idx}`}>{formatInsightItem(item)}</li>
                ))}
              </ul>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2 text-emerald-700">
                <Lightbulb className="h-3.5 w-3.5" />
                <h4 className="text-xs font-bold uppercase tracking-wide">
                  Scenario ideas
                </h4>
              </div>
              <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">
                {scenarioIdeas.map((item, idx) => (
                  <li key={`idea-${idx}`}>{formatInsightItem(item)}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
